import { useState } from 'react';

import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { Button } from '@/new/components/shadcn/button';
import { Label } from '@/new/components/shadcn/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/new/components/shadcn/select';
import { FilterOption } from '@/types';

const baseSortFieldOptions: FilterOption[] = [
  { label: 'Document Date', value: 'date' },
  { label: 'Upload Date', value: 'createdDate' },
  { label: 'Filename', value: 'name' },
  { label: 'Author', value: 'author' },
  { label: 'Type', value: 'type' },
  { label: 'Facts Count', value: 'factsCount' },
];

const sortOrderOptions: FilterOption[] = [
  { label: 'Descending', value: 'DESC' },
  { label: 'Ascending', value: 'ASC' },
];

export const DocSortPopup = ({
  activeSortBy,
  activeSortOrder,
  setSortBy,
  setSortOrder,
  handleClose,
  hasActiveSearch,
}: {
  activeSortBy: string;
  activeSortOrder: 'ASC' | 'DESC';
  setSortBy: (value: string) => void;
  setSortOrder: (value: 'ASC' | 'DESC') => void;
  handleClose: () => void;
  hasActiveSearch?: boolean;
}) => {
  const [tempSortBy, setTempSortBy] = useState<string>(activeSortBy);
  const [tempSortOrder, setTempSortOrder] = useState<'ASC' | 'DESC'>(activeSortOrder);

  // Add chunkMatchCount option when there's an active search
  const sortFieldOptions = hasActiveSearch
    ? [...baseSortFieldOptions, { label: 'Matches in Content', value: 'chunkMatchCount' }]
    : baseSortFieldOptions;

  const handleSubmit = () => {
    trackCustomEvent(TrackingEvents.ApplySortOnDocumentsPage);
    setSortBy(tempSortBy);
    setSortOrder(tempSortOrder);
    handleClose();
  };

  const handleReset = () => {
    setTempSortBy('');
    setTempSortOrder('DESC');
    setSortBy('');
    setSortOrder('DESC');
    handleClose();
  };

  const getCurrentSortField = () => {
    return sortFieldOptions.find((option) => option.value === tempSortBy)?.label || 'No sorting';
  };

  const getCurrentSortOrder = () => {
    return sortOrderOptions.find((option) => option.value === tempSortOrder)?.label || 'Descending';
  };

  return (
    <div className="p-6 text-sm">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Sort Documents</h2>
        <p className="mt-1 text-sm text-gray-500">Choose how to order your documents</p>
      </div>

      <div className="space-y-5">
        {/* Sort Field */}
        <div>
          <Label className="mb-2 block">Sort By</Label>
          <Select
            value={tempSortBy}
            onValueChange={(newValue) => {
              if (newValue) {
                setTempSortBy(newValue);
              }
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select sort field..." />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {sortFieldOptions.map((option) => (
                  <SelectItem value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          <div className="mt-2 text-xs text-gray-500">
            Currently: <span className="font-medium">{getCurrentSortField()}</span>
          </div>
        </div>

        {/* Sort Order - only show if a sort field is selected */}
        {tempSortBy && (
          <div>
            <Label className="mb-2 block">Order</Label>
            <Select
              value={tempSortOrder}
              onValueChange={(newValue) => {
                if (newValue) {
                  setTempSortOrder(newValue as 'ASC' | 'DESC');
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select sort order..." />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {sortOrderOptions.map((option) => (
                    <SelectItem value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            <div className="mt-2 text-xs text-gray-500">
              Currently: <span className="font-medium">{getCurrentSortOrder()}</span>
            </div>
          </div>
        )}
      </div>

      <div className="mt-8 flex gap-3 border-t border-gray-200 pt-4">
        <Button className="flex-1" variant="destructive" onClick={handleReset}>
          Clear
        </Button>
        <Button className="flex-1" onClick={handleSubmit}>
          Apply
        </Button>
      </div>
    </div>
  );
};
